/**
 * A dialog, and the "are you sure?" built on it.
 *
 * PRESENTATIONAL. It holds no data and fetches nothing; a screen that needs to
 * confirm a delete passes the handler in. `ConfirmDialog` additionally refuses
 * to close itself on Escape or a backdrop click while the action is in flight,
 * because a dialog that vanishes mid-delete leaves the user unsure whether the
 * thing they asked to remove is gone.
 */
import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** Disable Escape and backdrop dismissal — used while a confirm is running. */
  readonly locked?: boolean;
  readonly footer?: ReactNode;
  readonly className?: string;
}

/**
 * A dialog.
 *
 * Escape and a backdrop click both close it, unless `locked`. A dialog that can
 * be dismissed mid-action leaves the user unsure whether the thing they asked
 * for actually happened.
 */
export function Modal({ open, title, onClose, children, locked = false, footer, className }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !locked) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, locked, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={() => {
        if (!locked) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn('w-full max-w-lg rounded-lg border border-slate-700 bg-slate-900 p-4', className)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={locked}
            aria-label="Close"
            className="text-slate-500 hover:text-slate-200 disabled:opacity-40"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="mt-3 text-sm text-slate-300">{children}</div>
        {footer ? <div className="mt-4 flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  /** Style the confirm button as destructive. The message must justify it. */
  readonly destructive?: boolean;
  readonly busy?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Deliberately not `window.confirm()`.
 *
 * The native dialog cannot be styled, cannot explain what will happen to the
 * data, and on some platforms returns a promise-like object that reads as truthy
 * — so a "Cancel" is treated as "OK". A billing app that silently deletes a
 * customer on a mis-click is exactly the 1.6.x class of failure this project
 * exists to remove.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      locked={busy}
      className="max-w-md"
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={cn(
              'rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50',
              destructive ? 'bg-red-700 hover:bg-red-600' : 'bg-brand-500 hover:bg-brand-700',
            )}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {message}
    </Modal>
  );
}
